import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Loader2 } from "lucide-react";
import { api, API, getToken } from "@/lib/api";

export default function AiAssistant({ open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !sessionId) {
      (async () => {
        try {
          const { data } = await api.post("/ai/session", {});
          setSessionId(data.session_id);
          setMessages([{
            role: "assistant",
            content: "Hei! Jeg er Krafthub AI. Spør meg om kunder, fakturaer, forbruk eller avtaler."
          }]);
        } catch {}
      })();
    }
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            const payload = line.slice(6);
            if (payload === "[DONE]") continue;
            if (payload.startsWith("[ERROR]")) {
              acc += `\n\n⚠ ${payload}`;
            } else {
              acc += payload;
            }
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: acc };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠ Klarte ikke å kontakte AI-assistenten." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex" data-testid="ai-assistant-panel">
      <div className="absolute inset-0 -left-full bg-black/50 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
      <div className="relative w-[440px] max-w-[95vw] h-full bg-[#0a0a0a] border-l border-zinc-900 flex flex-col shadow-2xl">
        <div className="h-14 px-4 flex items-center justify-between border-b border-zinc-900">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
              <Sparkles className="h-4 w-4 text-black" />
            </div>
            <div>
              <div className="text-sm font-medium text-zinc-100 font-heading">Krafthub AI</div>
              <div className="text-[10px] text-zinc-500">Claude Sonnet 4.6 · norsk</div>
            </div>
          </div>
          <button onClick={() => onOpenChange(false)} className="p-1.5 hover:bg-zinc-900 rounded-md text-zinc-400" data-testid="ai-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-emerald-500/15 border border-emerald-500/25 text-emerald-50"
                  : "bg-zinc-900 border border-zinc-800 text-zinc-100"
              }`}>
                {m.content || (sending && i === messages.length - 1 ? (
                  <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
                ) : null)}
              </div>
            </div>
          ))}
        </div>

        <div className="p-3 border-t border-zinc-900">
          <div className="flex items-end gap-2">
            <textarea
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Spør AI-en… (Shift+Enter for linjeskift)"
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 resize-none focus:outline-none focus:border-emerald-500/50"
              data-testid="ai-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black transition-colors"
              data-testid="ai-send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
